import React, { useMemo } from 'react';
import { AlarmClockIcon } from 'lucide-react';
import { useCountdown } from '../hooks/useCountdown';
import { getOfferDeadline, spotsTakenPercent } from '../utils/offer';
function pad(value: number): string {
  return value.toString().padStart(2, '0');
}

/**
 * Compact urgency bar. It sits directly above the price card so the deadline
 * and the purchase button are visible at the same time.
 */
export function CountdownTimer() {
  const deadline = useMemo(() => getOfferDeadline(), []);
  const {
    hours,
    minutes,
    seconds,
    isExpired
  } = useCountdown(deadline);
  const units = [{
    value: hours,
    label: 'hrs'
  }, {
    value: minutes,
    label: 'min'
  }, {
    value: seconds,
    label: 'sec'
  }];
  return <div className="rounded-2xl bg-flame px-4 py-3.5 text-center" data-id="element-31" data-mp="15exwpjnelxdp">
      <p className="flex items-center justify-center gap-1.5 font-sans text-xs font-semibold uppercase tracking-[0.12em] text-shell" data-id="element-32" data-mp="34qiaz7re137b">
        <AlarmClockIcon className="h-3.5 w-3.5 text-lime" strokeWidth={2.4} aria-hidden="true" data-id="element-33" data-mp="2zsysfvnnq49k" />
        95% launch price ends in
      </p>

      {isExpired ? <p className="mt-2 font-display text-sm font-bold text-shell" data-id="element-34" data-mp="1h0jecf258195">
          Today’s launch price has ended.
        </p> : <>
          <div className="mt-2 flex items-center justify-center gap-1.5" aria-hidden="true" data-id="element-35" data-mp="171lqlecjkbr7">
            {units.map((unit, index) => <React.Fragment key={unit.label} data-id="element-36" data-mp="1ut9d7k2rt0no" data-mpi={index}>
                <div className="flex min-w-[3.6rem] items-baseline justify-center gap-1 rounded-lg bg-shell px-2 py-1.5" data-id="element-37" data-mp="1zqiyqon1gs68">
                  <span className="font-display text-xl font-extrabold leading-none tabular-nums text-ink" data-id="element-38" data-mp="1l0lj4x36dasn">
                    {pad(unit.value)}
                  </span>
                  <span className="font-sans text-xs font-semibold uppercase tracking-[0.06em] text-stone" data-id="element-39" data-mp="2dpira7do9r7o">
                    {unit.label}
                  </span>
                </div>
                {index < units.length - 1 ? <span className="font-display text-base font-bold text-shell/70" data-id="element-40" data-mp="2sc7n9z8ptm9b">
                    :
                  </span> : null}
              </React.Fragment>)}
          </div>
          <p className="sr-only" role="timer" data-id="element-41" data-mp="2ja87195iym7f">
            {hours} hours, {minutes} minutes and {seconds} seconds left at the
            launch price.
          </p>

          <div className="mt-3" data-id="element-42" data-mp="29ewwz452a360">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-shell/30" role="progressbar" aria-valuenow={spotsTakenPercent} aria-valuemin={0} aria-valuemax={100} aria-label="Launch price spots taken" data-id="element-43" data-mp="29fyargdgp3rf">
              <div className="h-full rounded-full bg-lime" style={{
            width: `${spotsTakenPercent}%`
          }} data-id="element-44" data-mp="1vknkyhe2r8tx" />
            </div>
            <p className="mt-1.5 font-sans text-xs font-medium text-shell" data-id="element-45" data-mp="1losx97uzunka">
              {spotsTakenPercent}% of launch spots taken — at midnight the price
              goes back up.
            </p>
          </div>
        </>}
    </div>;
}